import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Body, Heading, Muted } from "../../components/Typography";
import { ApiError, api } from "../../lib/api";
import type { StoreResponse, UpdateStoreRequest } from "../../lib/api";
import { WatchInstructionsEditor } from "../CameraView/WatchInstructionsEditor";
import styles from "./SettingsPanel.module.css";

type Section = "store" | "watch" | "danger";

const SECTIONS: { key: Section; label: string }[] = [
  { key: "store", label: "Store" },
  { key: "watch", label: "Watch instructions" },
  { key: "danger", label: "Danger zone" },
];

type FormState = {
  name: string;
  address: string;
  description: string;
};

const toForm = (store: StoreResponse): FormState => ({
  name: store.name,
  address: store.address ?? "",
  description: store.description ?? "",
});

const diff = (form: FormState, store: StoreResponse): UpdateStoreRequest => {
  const patch: UpdateStoreRequest = {};
  const trimmedName = form.name.trim();
  if (trimmedName && trimmedName !== store.name) patch.name = trimmedName;
  const address = form.address.trim();
  if (address !== (store.address ?? "")) patch.address = address === "" ? null : address;
  const description = form.description.trim();
  if (description !== (store.description ?? "")) {
    patch.description = description === "" ? null : description;
  }
  return patch;
};

type SettingsPanelProps = {
  store: StoreResponse;
  currentUserId: string | undefined;
  onStoreChanged: (store: StoreResponse) => void;
};

export const SettingsPanel = ({ store, currentUserId, onStoreChanged }: SettingsPanelProps) => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(() => toForm(store));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeSection, setActiveSection] = useState<Section>("store");

  const isOwner = store.owner_id === currentUserId;
  // Admin-level like every store setting (owner = admin + delete + transfer); a plain
  // member's role comes from the roster, as the Members and Billing tabs read it.
  const [canManageWatch, setCanManageWatch] = useState<boolean | null>(null);
  useEffect(() => {
    if (isOwner) {
      setCanManageWatch(true);
      return;
    }
    let alive = true;
    api
      .listMembers(store.id)
      .then((members) => {
        if (!alive) return;
        const role = members.find((m) => m.user_id === currentUserId)?.role;
        setCanManageWatch(role === "owner" || role === "admin");
      })
      .catch(() => {
        if (alive) setCanManageWatch(false);
      });
    return () => {
      alive = false;
    };
  }, [store.id, currentUserId, isOwner]);
  const patch = diff(form, store);
  const dirty = Object.keys(patch).length > 0;
  const overviewPath = `/stores/${store.id}`;

  const handleSave = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateStore(store.id, patch);
      onStoreChanged(updated);
      navigate(overviewPath);
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = (): void => {
    setForm(toForm(store));
    navigate(overviewPath);
  };

  const handleDelete = async (): Promise<void> => {
    const ok = window.confirm(
      `Delete "${store.name}"? This also deletes every NVR and camera under it.`,
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await api.deleteStore(store.id);
      navigate("/stores", { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't delete.");
      setDeleting(false);
    }
  };

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <nav aria-label="Settings sections">
          <ul className={styles.navList}>
            {SECTIONS.map(({ key, label }) => (
              <li key={key}>
                <button
                  type="button"
                  className={styles.navItem}
                  data-active={activeSection === key}
                  onClick={() => setActiveSection(key)}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <section className={styles.main}>
        {activeSection === "store" ? (
          <StoreProfileForm
            storeId={store.id}
            form={form}
            setForm={setForm}
            onSubmit={handleSave}
            onDiscard={handleDiscard}
            saving={saving}
            dirty={dirty}
            error={error}
          />
        ) : activeSection === "watch" ? (
          <WatchSection storeId={store.id} canManage={canManageWatch} />
        ) : isOwner ? (
          <div className={styles.dangerZone}>
            <div className={styles.dangerCopy}>
              <span className={styles.dangerTitle}>Delete this store</span>
              <Body>
                <Muted>
                  Permanently removes the store, every NVR and camera under it, and any recorded
                  events. Members lose access immediately.
                </Muted>
              </Body>
            </div>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete store"}
            </Button>
          </div>
        ) : (
          <div className={styles.stub}>
            <Body>
              <Muted>Only the owner can delete this store.</Muted>
            </Body>
          </div>
        )}
      </section>
    </div>
  );
};

type StoreProfileFormProps = {
  storeId: string;
  form: FormState;
  setForm: (updater: (prev: FormState) => FormState) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onDiscard: () => void;
  saving: boolean;
  dirty: boolean;
  error: string | null;
};

const StoreProfileForm = ({
  storeId,
  form,
  setForm,
  onSubmit,
  onDiscard,
  saving,
  dirty,
  error,
}: StoreProfileFormProps) => {
  const [copied, setCopied] = useState(false);

  const update = (key: keyof FormState, value: string): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const copyStoreId = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(storeId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <header className={styles.mainHeader}>
        <div className={styles.mainHeaderCopy}>
          <span className={styles.mainKicker}>Store profile</span>
          <Heading level={2}>The basics about this shop</Heading>
          <Body>
            <Muted>
              Shown to members and used by the AI agents when they write reports about what they
              see.
            </Muted>
          </Body>
        </div>
        <div className={styles.mainHeaderActions}>
          <Button variant="outline" onClick={onDiscard} disabled={saving}>
            Discard
          </Button>
          <Button type="submit" form="store-profile" disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </header>

      <form id="store-profile" className={styles.form} onSubmit={onSubmit} noValidate>
        <div className={styles.field}>
          <label htmlFor="store-id" className={styles.label}>
            Store ID
          </label>
          <div className={styles.copyRow}>
            <Input id="store-id" name="store-id" value={storeId} readOnly />
            <Button type="button" variant="outline" onClick={copyStoreId}>
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="name" className={styles.label}>
            Store name
          </label>
          <Input
            id="name"
            name="name"
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            disabled={saving}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="address" className={styles.label}>
            Address
          </label>
          <Input
            id="address"
            name="address"
            value={form.address}
            onChange={(e) => update("address", e.target.value)}
            disabled={saving}
            invalid={form.address.trim() === ""}
          />
          {form.address.trim() === "" && (
            <span className={styles.warn}>Add the shop address before enabling AI reports.</span>
          )}
        </div>

        <div className={styles.field}>
          <label htmlFor="description" className={styles.label}>
            Description
          </label>
          <textarea
            id="description"
            name="description"
            className={styles.textarea}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            disabled={saving}
          />
          <span className={styles.help}>
            Used by agents to reason about what normal looks like.
          </span>
        </div>

        {error && (
          <div role="alert" className={styles.error}>
            {error}
          </div>
        )}
      </form>
    </>
  );
};

type WatchSectionProps = {
  storeId: string;
  /** null while the member roster is still loading. */
  canManage: boolean | null;
};

const WatchSection = ({ storeId, canManage }: WatchSectionProps) => (
  <>
    <header className={styles.mainHeader}>
      <div className={styles.mainHeaderCopy}>
        <span className={styles.mainKicker}>Watch instructions</span>
        <Heading level={2}>What should the AI watch for?</Heading>
        <Body>
          <Muted>
            These rules apply to every camera in this store; rules about one camera live on that
            camera's page. Each one is reviewed and rewritten before it takes effect.
          </Muted>
        </Body>
      </div>
    </header>
    {canManage === null ? (
      <Body>
        <Muted>Loading…</Muted>
      </Body>
    ) : canManage ? (
      <WatchInstructionsEditor scope={{ kind: "store", id: storeId }} />
    ) : (
      <div className={styles.stub}>
        <Body>
          <Muted>Only the store's admins can change what the AI watches for.</Muted>
        </Body>
      </div>
    )}
  </>
);
